// @givmo/board — the words of directors' terms and elections (CBG-YR3-TERMS, -ELECT, -ANNUAL; the
// backend's contract `agent_docs/contracts/board-terms.md` §11, the approved frames t1–t8).
//
// Every line here is on its copy page (`2026-10-14-yr3terms-copy-round/copy-review.html`),
// approved in copy round CBG-YR3-COPY1 (Jonathan, 2026-10-14 21:07 PDT). The lines this cut
// REUSES are approved and stay in their own modules, imported where they serve: the holdover's
// "Term ended {date} – still serving" and the member page's "Since {date} · Term ended {date} – still
// serving" (`officer-roles-l3-words.ts`, CBG-YR3-ROLES-COPY3 and HOLDOVER-PAGE), the titles' "Held
// from" / "Held to" and the menus' Rename · Move up · Move down · Retire (`officer-roles-l2-words.ts`),
// and the history's field word "term" (`roster-history.ts`, copy round 3).
//
// Nothing here says eligible, term-limited, expired, lapsed, overdue, late, must, should, required,
// "your bylaws require", can't vote or invalid, and no line counts the days left (§11.3).

const plural = (count: number, one: string, many: string) => (count === 1 ? one : many);

export const termsWords = {
  // ── A seat's terms (the member page, "Their service", t1) ───────────────────────────────────────
  range: (from: string, to: string) => `${from} – ${to}`,
  from: (from: string) => `From ${from}`,
  until: (to: string) => `Until ${to}`,
  endsOn: (date: string) => `Term ends ${date}`,
  notCounted: 'Not counted toward the limit',
  electedAt: 'Elected at the',
  electedByConsent: 'Elected by the written consent',
  fromTheBook: 'From the minute book:',
  enteredBy: (name: string) => `Entered by ${name}`,
  carriedOver: 'From the dates this seat had before Board kept terms',
  /** The board's own count beside the board's own recorded limit: plan §3.4's "against the … you
   *  recorded" shape, a count and no conclusion. Officers only. */
  counted: (counted: number, limit: number, citation: string) =>
    `Consecutive terms: ${counted} counted, against the limit you recorded (${limit}, ${citation}).`,
  recordTerm: 'Record a term',
  correct: 'Correct',
  correctFor: (dates: string) => `Correct the term ${dates}`,

  // ── Record a term / Correct (no frame drew these dialogs) ──────────────────────────────────────
  recordTitle: 'Record a term',
  correctTitle: 'Correct this term',
  start: 'Start',
  end: 'End',
  classLabel: 'Class',
  noClass: 'No class',
  countsTowardLimit: 'Counts toward the limit',
  note: 'Note (optional)',
  bookRecord: 'From the minute book (optional)',
  noBookRecord: 'Not from a record',
  datesNote: 'Your organization’s own dates. Leave one empty if it isn’t recorded.',
  needADate: 'Record a start, an end, or both.',
  endBeforeStart: 'A term’s end can’t be before its start.',
  recordIt: 'Record it',
  saveIt: 'Save',
  removeTerm: 'Remove this term',
  removeTitle: 'Remove this term?',
  removeBody:
    'Use this for a term recorded by mistake, such as for the wrong person. The motion, written consent or record it came from doesn’t change.',
  removeIt: 'Remove it',
  termRecorded: 'Term recorded.',
  termCorrected: 'Term corrected.',
  termRemoved: 'Term removed.',
  termFailed: 'We couldn’t save that term. Please try again.',

  // ── The roster's Term column (t3) ──────────────────────────────────────────────────────────────
  rosterEnds: (date: string) => `Ends ${date}`,
  rosterEnded: (date: string) => `Ended ${date} – still serving`,

  // ── Governance › Terms (t2) ────────────────────────────────────────────────────────────────────
  tab: 'Terms',
  termsLead:
    'Your board’s term rules — how long directors and officers serve, and how many terms in a row — each with the place in your governing documents it comes from.',
  directorsTerm: 'Directors’ term',
  consecutiveTerms: 'Consecutive terms',
  officersTerm: 'Officers’ term',
  ruleCount: (kind: 'years' | 'terms') => (kind === 'years' ? 'Years' : 'Terms in a row'),
  ruleRecorded: 'Rule recorded.',
  classes: 'Classes',
  classesLine: 'Your board’s own names for its classes. Each director’s term can name one.',
  noClasses: 'No classes recorded.',
  addClass: 'Add a class',
  className: 'Name',
  classNameNote: 'As your board calls it, for example “Class A”.',
  renameClass: (label: string) => `Rename ${label}`,
  retireClass: (label: string) => `Retire ${label}?`,
  retireClassBody:
    'It stays on every term that names it, and isn’t offered for new terms. You can add a class with the same name later.',
  classAdded: 'Class added.',
  classRenamed: 'Class renamed.',
  classMoved: 'Class moved.',
  classRetired: 'Class retired.',
  classFailed: 'We couldn’t save that class. Please try again.',
  classesLoadFailed: 'We couldn’t load your classes.',

  // ── The officers' panel on Meetings (t7) ───────────────────────────────────────────────────────
  panelTitle: 'Terms ending',
  panelLine: 'Terms you recorded that have ended, or end in the next three months.',
  directors: 'Directors',
  officers: 'Officers',
  annualMeetingOn: (date: string) => `Your annual meeting is on ${date}.`,

  // ── The annual meeting's suggestion (t4) ───────────────────────────────────────────────────────
  suggestionTitle: 'Terms ending',
  suggestionLine:
    'From your roster: terms that have ended, or end within three months of this meeting.',
  addElections: 'Add elections to the agenda',
  /** When the annual template's own sections are on the agenda, the names go into them. */
  addNamesTo: (sections: string) => `Add these names to ${sections}`,
  quoted: (title: string) => `“${title}”`,
  /** An officer's end Board worked out from the officers' recorded term length, with its source. */
  fromOfficerTerm: (years: number, citation: string) =>
    `from the ${years}-year officers’ term you recorded, ${citation}`,
  notForThisMeeting: 'Not for this meeting',
  showAgain: 'Show the terms ending',
  fromTheTerms: 'From the terms on your roster',
  electionsAdded: 'Elections added to the agenda.',
  suggestionFailed: 'We couldn’t change the agenda. Please try again.',

  // ── Recording an election's result (t5) ────────────────────────────────────────────────────────
  recordResult: 'Record the result on the roster',
  carried: (date: string) => `Carried · ${date}`,
  executed: (date: string) => `Executed · ${date}`,
  directorsElected: 'Directors elected',
  officersElected: 'Officers elected',
  title: 'Title',
  endFromLength: (years: number, citation: string) =>
    `${years} ${plural(years, 'year', 'years')} from the start: the term length you recorded (${citation}).`,
  withThisOne: (counted: number, limit: number, citation: string) =>
    `With this one, ${counted} consecutive ${plural(counted, 'term', 'terms')} counted, against the limit you recorded (${limit}, ${citation}).`,
  addDirector: 'Add a director',
  addOfficer: 'Add an officer',
  addSomeoneNew: 'Add someone new',
  chooseTitle: 'Choose a title for each officer.',
  /** The backend's approved title-dates refusal (CBG-YR3-ROLES-COPY4), said before it is sent. */
  heldToBeforeFrom: 'A title’s “Held to” date can’t be before its “Held from” date.',
  chooseDirector: 'Choose a director',
  choosePerson: 'Choose a person',
  removeRow: (name: string) => `Remove ${name}`,
  resultNoteMotion:
    'Saving records these terms on your roster, linked to this motion. The motion and the minutes don’t change.',
  resultNoteConsent:
    'Saving records these terms on your roster, linked to this written consent. The consent doesn’t change.',
  recordOnRoster: 'Record on the roster',
  resultRecorded: 'Recorded on the roster.',
  resultFailed: 'We couldn’t record that on the roster. Please try again.',
} as const;
